import { FieldResult, KeyType, StickRow } from "../models/stickData";

/**
 * Sentinel value used by the API to indicate missing/null data
 * This value appears in intData, dateData, and floatData arrays
 */
export const NULL_SENTINEL_VALUE = -999999001;

/**
 * Converts columnar stick data from the API to row-based format for Ant Design table
 *
 * The API returns data in columnar format where each FieldResult contains:
 * - field: the field name
 * - Arrays of data (intData, dateData, floatData) where each index represents a row
 * - keyType: indicates which array contains the data
 *
 * This function transposes the data so each row represents a stick with all its field values
 *
 * @param fieldResults - Array of FieldResult from the API response
 * @param stickIds - Optional array of stick IDs to use for the rows (defaults to stick-0, stick-1, etc.)
 * @returns Array of StickRow objects for the table
 */
export function convertStickDataToRows(
  fieldResults: FieldResult[],
  stickIds?: string[]
): StickRow[] {
  if (!fieldResults || fieldResults.length === 0) {
    return [];
  }

  // Determine the number of rows from the first field result
  // Check all data arrays to find the maximum length
  const firstField = fieldResults[0];
  const rowCount = Math.max(
    firstField.intData?.length || 0,
    firstField.dateData?.length || 0,
    firstField.floatData?.length || 0,
    firstField.value?.length || 0
  );

  if (rowCount === 0) {
    return [];
  }
  // Initialize rows with unique IDs
  const rows: StickRow[] = Array.from({ length: rowCount }, (_, index) => ({
    id: stickIds?.[index],
    key: index.toString() // Ant Design table key
  }));

  // Populate each row with field values
  fieldResults.forEach((fieldResult) => {
    const { field, keyType, intData, dateData, floatData, value } = fieldResult;

    for (let i = 0; i < rowCount; i++) {
      let fieldValue: string | number | undefined;

      switch (keyType) {
        case KeyType.IntKey: {
          // Treat -999999001 as null/missing value
          const intValue = intData?.[i];
          fieldValue = intValue === NULL_SENTINEL_VALUE ? undefined : intValue;
          break;
        }
        case KeyType.DateKey: {
          // Convert Unix timestamp to YYYY-MM-DD format
          const timestamp = dateData?.[i];
          fieldValue = timestamp ? formatTimestampToISODate(timestamp) : undefined;
          break;
        }
        case KeyType.FloatKey: {
          // Treat -999999001 as null/missing value
          const floatValue = floatData?.[i];
          fieldValue = floatValue === NULL_SENTINEL_VALUE ? undefined : floatValue;
          break;
        }
        case KeyType.CategoricalKey: {
          // For categorical data, intData contains indices into the value array
          const categoryIndex = intData?.[i];
          if (
            categoryIndex !== undefined &&
            categoryIndex !== NULL_SENTINEL_VALUE &&
            value
          ) {
            fieldValue = value[categoryIndex];
          }
          break;
        }
        default:
          fieldValue = undefined;
      }

      // Fallback: If we have a single categorical value, apply it to all rows
      // This handles cases where header fields have the same value for all sticks
      if (
        value &&
        value.length === 1 &&
        (fieldValue === null || fieldValue === undefined)
      ) {
        fieldValue = value[0];
      }

      rows[i][field] = fieldValue;
    }
  });

  return rows;
}

/**
 * Converts Unix timestamp to ISO date string (YYYY-MM-DD)
 *
 * @param timestamp - Unix timestamp in seconds
 * @returns ISO date string in YYYY-MM-DD format
 */
export function formatTimestampToISODate(timestamp: number): string {
  // Treat NULL_SENTINEL_VALUE as missing/null date
  if (!timestamp || timestamp === NULL_SENTINEL_VALUE) return "";

  try {
    // Convert Unix timestamp (seconds) to milliseconds
    const date = new Date(timestamp * 1000);

    // Format as YYYY-MM-DD using UTC methods to avoid timezone issues
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    return "";
  }
}
